import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';

import { getContentAppearanceFromQueryData, contentAppearanceQuery } from './contentAppearance.ts';
import type { ContentAppearance } from './contentAppearanceTypes.ts';
import { ContentAppearancePropertyKey, ContentAppearanceType } from './contentAppearanceTypes.ts';
import type {
	contentAppearanceQuery$data,
	contentAppearanceQuery$variables,
} from './__generated__/contentAppearanceQuery.graphql.ts';

type ContentAppearanceContextType = {
	contentAppearance: ContentAppearance;
	setContentAppearance(contentAppearance: ContentAppearance): void;
	contentAppearanceLoading: boolean;
};
const ContentAppearanceContext = React.createContext<ContentAppearanceContextType>({
	contentAppearance: {
		appearance: ContentAppearanceType.DEFAULT,
		version: 0,
	},
	setContentAppearance: () => {},
	contentAppearanceLoading: false,
});

type ContentAppearanceContextProviderProps = {
	contentId: string;
	children: React.ReactNode;
};
export const ContentAppearanceContextProvider = ({
	contentId,
	children,
}: ContentAppearanceContextProviderProps): React.JSX.Element => {
	const [contentAppearanceOverride, setContentAppearanceOverride] = useState<ContentAppearance>();

	// Clear the override state when navigating to a different page
	useEffect(() => {
		if (fg('platform_editor_appearance_override_reset')) {
			setContentAppearanceOverride(undefined);
		}
	}, [contentId]);

	const [{ collabEditProvider }] = useNativeCollabState();
	// Subscribe to metadata:changed events to update the content appearance for collaborators
	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleMetadataChanged = (metadata: any) => {
			const widthValue = metadata?.editorWidth?.value;
			const version = metadata?.editorWidth?.version;

			if (widthValue) {
				setContentAppearanceOverride((currentAppearance) => {
					if (!currentAppearance || version > currentAppearance.version) {
						return {
							appearance: widthValue,
							version,
						};
					} else {
						return currentAppearance;
					}
				});
			}
		};

		collabEditProvider.on('metadata:changed', handleMetadataChanged);
		return () => {
			collabEditProvider.off('metadata:changed', handleMetadataChanged);
		};
	}, [collabEditProvider]);

	/*
		Previous check was:
		const draftAppearanceQuery = getContentAppearanceQuery(ContentAppearancePropertyKey.DRAFT);
		which always returns contentAppearanceQuery
	*/
	const { data: initialContentAppearanceData, loading: initialContentAppearanceLoading } =
		useQueryInterop<contentAppearanceQuery$data, contentAppearanceQuery$variables>(
			contentAppearanceQuery,
			{
				variables: {
					contentId,
					status: ['current', 'draft'],
				},
			},
		);

	const context = useMemo(() => {
		const initialContentAppearance = getContentAppearanceFromQueryData(
			initialContentAppearanceData,
			ContentAppearancePropertyKey.DRAFT,
		);
		return {
			contentAppearance: contentAppearanceOverride || initialContentAppearance,
			setContentAppearance: setContentAppearanceOverride,
			contentAppearanceLoading: initialContentAppearanceLoading,
		};
	}, [initialContentAppearanceData, initialContentAppearanceLoading, contentAppearanceOverride]);

	return (
		<ContentAppearanceContext.Provider value={context}>
			{children}
		</ContentAppearanceContext.Provider>
	);
};

type ContentAppearanceContextConsumerProps = {
	children: (props: ContentAppearanceContextType) => JSX.Element;
};
export const ContentAppearanceContextConsumer = ({
	children,
}: ContentAppearanceContextConsumerProps): JSX.Element => {
	const context = useContentAppearance();
	return children(context);
};

export const useContentAppearance = (): ContentAppearanceContextType => {
	return useContext(ContentAppearanceContext);
};
